"use client";

import { config } from "@/config/env";

export interface WatermarkFormatSettings {
  logoEnabled: boolean;
  introEnabled: boolean;
  opacity: number;
  alignment: string;
  padding: number;
  size: number;
  introTitle?: string;
  introStyle?: string;
  introAnimation?: string;
  introBgColor?: string;
  introBgOpacity?: number;
  introTextColor?: string;
  introFont?: string;
  introAlignment?: string;

  introFrameUrls?: string[];  // Individual frame PNG URLs for the intro animation
  contactCard?: string;
}

export interface WatermarkTemplate {
  id: string;
  userId: string;
  name: string;
  watermarkUrl: string;
  settingsLandscape: WatermarkFormatSettings;
  settingsPortrait: WatermarkFormatSettings;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWatermarkTemplateRequest {
  name: string;
  watermarkUrl: string;
  settingsLandscape: WatermarkFormatSettings;
  settingsPortrait: WatermarkFormatSettings;
}

export interface UpdateWatermarkTemplateRequest {
  name: string;
  watermarkUrl: string;
  settingsLandscape: WatermarkFormatSettings;
  settingsPortrait: WatermarkFormatSettings;
}

async function authFetch(
  endpoint: string,
  getToken: () => Promise<string | null>,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getToken();
  if (!token) throw new Error("Not authenticated");

  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...((options.headers as Record<string, string>) ?? {}),
    },
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || `Request failed: ${response.status}`);
  }

  return response;
}

/** List all watermark templates for the current user */
export async function listWatermarkTemplates(
  getToken: () => Promise<string | null>
): Promise<{ templates: WatermarkTemplate[] }> {
  const response = await authFetch("/templates/watermark", getToken, { method: "GET" });
  return response.json();
}

/** Create a new watermark template */
export async function createWatermarkTemplate(
  body: CreateWatermarkTemplateRequest,
  getToken: () => Promise<string | null>
): Promise<{ template: WatermarkTemplate }> {
  const response = await authFetch("/templates/watermark", getToken, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return response.json();
}

/** Update an existing watermark template by ID */
export async function updateWatermarkTemplate(
  id: string,
  body: UpdateWatermarkTemplateRequest,
  getToken: () => Promise<string | null>
): Promise<{ template: WatermarkTemplate }> {
  const response = await authFetch(`/templates/watermark/${id}`, getToken, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return response.json();
}

/** Delete a watermark template by ID */
export async function deleteWatermarkTemplate(
  id: string,
  getToken: () => Promise<string | null>
): Promise<{ success: boolean }> {
  const response = await authFetch(`/templates/watermark/${id}`, getToken, { method: "DELETE" });
  return response.json();
}
